// ═══════════════════════════════════════════════════════════════════════════════
// 360-IF Neural Network  (V→I→H  classifier)
// Extracted from 360if_ai_core_v76_exp5e.html (lines 15901-16458)
//
// Pure math — no DOM, no UI.  All side-effects removed.
// Call-sites provide their own progress / UI callbacks.
// ═══════════════════════════════════════════════════════════════════════════════

import { C } from './constants.js';

// ── state ──────────────────────────────────────────────────────────────────
let qaPairs      = [];
let trained      = false;
let labels       = [];
let W_vi, W_ih, W_ho;          // weight matrices
let V_vi, V_ih, V_ho;          // momentum buffers
const MOMENTUM   = 0.9;

let domainVocabulary = [];      // [{word, count, importance}, ...]
let domainTopics     = [];

// Vocabulary statistics for IDF-like weighting
let vocabStats = { docCount: 0, termDocs: {} };

// ── stop words ─────────────────────────────────────────────────────────────
const STOP_WORDS = new Set([
  'the','a','an','is','are','was','were','be','been','being',
  'have','has','had','do','does','did','will','would','could',
  'should','may','might','must','shall','can','need','dare',
  'ought','used','to','of','in','for','on','with','at','by',
  'from','as','into','through','during','before','after','above',
  'below','between','under','again','further','then','once',
  'here','there','when','where','why','how','all','each','few',
  'more','most','other','some','such','no','nor','not','only',
  'own','same','so','than','too','very','just','and','but',
  'if','or','because','until','while','this','that','these',
  'those','what','which','who','whom','its','it','he','she',
  'they','them','his','her','their','my','your','our','i','me',
  'you','we','us','about','also','get','got','like','make',
  'made','see','seen','know','known','take','taken','come',
  'came','go','went','gone','say','said','tell','told'
]);

// ── helpers ────────────────────────────────────────────────────────────────

function extractWords(text) {
  return text.toLowerCase()
    .replace(/[^a-zα-ωφ0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 1);
}

function guessDomain(topics) {
  if (topics.length === 0) return 'General';
  const topTopics = topics.slice(0, 3).join('/');
  return topTopics ? `${topTopics} related` : 'General';
}

// ── vocabulary learning ────────────────────────────────────────────────────

export function buildDomainVocabulary(pairs) {
  const source = pairs || qaPairs;
  if (source.length === 0) return { vocabulary: [], topics: [], domain: 'General' };

  const wordCounts = {};
  const wordInQuestions = {};
  const wordInAnswers = {};

  source.forEach(qa => {
    const qWords = extractWords(qa.q);
    const aWords = extractWords(qa.a);
    qWords.forEach(word => {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
      wordInQuestions[word] = (wordInQuestions[word] || 0) + 1;
    });
    aWords.forEach(word => {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
      wordInAnswers[word] = (wordInAnswers[word] || 0) + 1;
    });
  });

  const vocabulary = [];
  for (const [word, count] of Object.entries(wordCounts)) {
    if (STOP_WORDS.has(word) || word.length < 2 || /^\d+$/.test(word)) continue;
    const qCount = wordInQuestions[word] || 0;
    const aCount = wordInAnswers[word] || 0;
    const importance =
      (count / source.length) *
      (1 + (qCount > 0 && aCount > 0 ? 1 : 0)) *
      (1 + Math.log(count + 1));
    vocabulary.push({ word, count, qCount, aCount, importance });
  }

  vocabulary.sort((a, b) => b.importance - a.importance);
  domainVocabulary = vocabulary.slice(0, 64);
  domainTopics = vocabulary
    .filter(v => v.qCount > 0 && v.aCount > 0)
    .slice(0, 10)
    .map(v => v.word);

  const domain = guessDomain(domainTopics);
  return { vocabulary: domainVocabulary, topics: domainTopics, domain };
}

// ── IDF ────────────────────────────────────────────────────────────────────

function updateVocabStats() {
  vocabStats = { docCount: qaPairs.length, termDocs: {} };
  qaPairs.forEach(qa => {
    const terms = new Set(qa.q.toLowerCase().split(/\s+/));
    terms.forEach(term => {
      vocabStats.termDocs[term] = (vocabStats.termDocs[term] || 0) + 1;
    });
  });
}

function getIDF(term) {
  const df = vocabStats.termDocs[term] || 0;
  if (df === 0 || vocabStats.docCount === 0) return 1;
  return Math.log(vocabStats.docCount / df) + 1;
}

// ── weight init ────────────────────────────────────────────────────────────

export function initWeights() {
  W_vi = []; V_vi = [];
  for (let i = 0; i < C.I; i++) {
    const row  = new Float64Array(C.V);
    const vRow = new Float64Array(C.V);
    const scale = Math.sqrt(2 / C.V) * C.ALPHA;
    for (let j = 0; j < C.V; j++) row[j] = (Math.random() - 0.5) * 2 * scale;
    W_vi.push(row); V_vi.push(vRow);
  }
  W_ih = []; V_ih = [];
  for (let i = 0; i < C.H; i++) {
    const row  = new Float64Array(C.I);
    const vRow = new Float64Array(C.I);
    const scale = Math.sqrt(2 / C.I) * C.ALPHA;
    for (let j = 0; j < C.I; j++) row[j] = (Math.random() - 0.5) * 2 * scale;
    W_ih.push(row); V_ih.push(vRow);
  }
  W_ho = []; V_ho = [];
}

function initOutputWeights(numClasses) {
  W_ho = []; V_ho = [];
  for (let i = 0; i < numClasses; i++) {
    const row  = new Float64Array(C.H);
    const vRow = new Float64Array(C.H);
    const scale = Math.sqrt(2 / C.H);
    for (let j = 0; j < C.H; j++) row[j] = (Math.random() - 0.5) * 2 * scale;
    W_ho.push(row); V_ho.push(vRow);
  }
}

// Auto-init weights on module load
initWeights();

// ── encode  (text → V-dim vector) ─────────────────────────────────────────

export function encode(text) {
  const v = new Float64Array(C.V);
  text = text.toLowerCase();

  // Character frequencies (0-25)
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c >= 97 && c <= 122) v[c - 97] += 1;
  }
  let charSum = 0;
  for (let i = 0; i < 26; i++) charSum += v[i];
  if (charSum > 0) for (let i = 0; i < 26; i++) v[i] /= charSum;

  // Word features (26-27)
  const words = text.split(/\s+/).filter(w => w.length > 0);
  v[26] = Math.min(words.length / 20, 1);
  v[27] = Math.min(text.length / 200, 1);

  // Question-type detection (28-35)
  const questionTypes = {
    28: /^(what|which)/i,
    29: /^(how|why)/i,
    30: /^(is|are|do|does|can|could)/i,
    31: /^(who|where|when)/i,
    32: /(compare|difference|vs|versus|between)/i,
    33: /(summarize|summary|overview|about)/i,
    34: /(optimal|best|ideal|recommend)/i,
    35: /(increase|decrease|change|if|hypothetical)/i
  };
  for (const [idx, pattern] of Object.entries(questionTypes)) {
    if (pattern.test(text)) v[parseInt(idx)] = 1;
  }

  // Dynamic domain vocabulary (36-99)
  if (domainVocabulary.length > 0) {
    for (let i = 0; i < Math.min(domainVocabulary.length, 64); i++) {
      const term = domainVocabulary[i];
      if (text.includes(term.word)) {
        const idf = getIDF(term.word);
        v[36 + i] = Math.min(idf * term.importance, 2);
      }
    }
  }

  // Bigram features (100-118)
  for (let i = 0; i < text.length - 1; i++) {
    const bigram = (text.charCodeAt(i) * 31 + text.charCodeAt(i + 1)) % 19;
    const posWeight = 1 + 0.5 * (1 - i / text.length);
    v[100 + bigram] += 0.1 * posWeight;
  }

  // Trigram features (119-V)
  const trigramSlots = Math.min(19, C.V - 119);
  for (let i = 0; i < text.length - 2; i++) {
    const trigram = (text.charCodeAt(i) * 961 + text.charCodeAt(i + 1) * 31 + text.charCodeAt(i + 2)) % trigramSlots;
    v[119 + trigram] += 0.1;
  }

  // L2 normalize
  let norm = 0;
  for (let i = 0; i < C.V; i++) norm += v[i] * v[i];
  norm = Math.sqrt(norm) || 1;
  for (let i = 0; i < C.V; i++) v[i] /= norm;

  return v;
}

// ── forward  (V→I→H→output) ───────────────────────────────────────────────

export function forward(input) {
  // Layer 1: V → I (visible → interface)
  const h1 = new Float64Array(C.I);
  for (let i = 0; i < C.I; i++) {
    let sum = 0;
    for (let j = 0; j < C.V; j++) sum += input[j] * W_vi[i][j];
    h1[i] = Math.tanh(sum);
  }

  // Layer 2: I → H (interface → hidden)
  const h2 = new Float64Array(C.H);
  for (let i = 0; i < C.H; i++) {
    let sum = 0;
    for (let j = 0; j < C.I; j++) sum += h1[j] * W_ih[i][j];
    h2[i] = Math.tanh(sum);
  }

  // Layer 3: H → numClasses (hidden → output)
  const logits = new Float64Array(W_ho.length);
  for (let i = 0; i < W_ho.length; i++) {
    let sum = 0;
    for (let j = 0; j < C.H; j++) sum += h2[j] * W_ho[i][j];
    logits[i] = sum;
  }

  return { h1, h2, logits };
}

// ── softmax / cross-entropy ────────────────────────────────────────────────

export function softmax(logits) {
  const max = Math.max(...logits);
  const exps = logits.map(l => Math.exp(l - max));
  const sum = exps.reduce((a, b) => a + b, 0);
  return exps.map(e => e / sum);
}

export function crossEntropy(probs, targetIdx) {
  return -Math.log(probs[targetIdx] + 1e-10);
}

// ── cosine similarity ──────────────────────────────────────────────────────

export function cosineSimilarity(a, b) {
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot  += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  return dot / (Math.sqrt(normA) * Math.sqrt(normB) + 1e-10);
}

// ── semantic search ────────────────────────────────────────────────────────

export function semanticSearch(queryEmbedding, topN = 3) {
  if (!trained || qaPairs.length === 0) return [];
  const results = [];
  qaPairs.forEach((qa, idx) => {
    const qInput = encode(qa.q);
    const { h2 } = forward(qInput);
    const similarity = cosineSimilarity(queryEmbedding, h2);
    results.push({ question: qa.q, answer: qa.a, similarity, idx });
  });
  results.sort((a, b) => b.similarity - a.similarity);
  return results.slice(0, topN);
}

// ── train  (async, yields to caller via callbacks) ─────────────────────────
// Returns a Promise that resolves with { accuracy, loss, epochs, labels }.

let isTraining = false;

/**
 * @param {Object}   opts
 * @param {Array}    opts.pairs         Q&A pairs  [{q, a}, ...]
 * @param {number}   [opts.epochs=100]
 * @param {number}   [opts.learningRate=0.01]
 * @param {Function} [opts.onProgress]  (epoch, totalEpochs, loss, accuracy) => void
 * @param {Function} [opts.onFinish]    ({accuracy, loss, epochs, labels}) => void
 * @returns {Promise<{accuracy:number, loss:number, epochs:number, labels:string[]}>}
 */
export function train(opts = {}) {
  return new Promise((resolve, reject) => {
    const pairs = opts.pairs || qaPairs;
    if (pairs.length < 2) {
      return reject(new Error('Need at least 2 Q&A pairs to train.'));
    }
    if (isTraining) {
      return reject(new Error('Training already in progress.'));
    }

    isTraining = true;
    qaPairs = pairs;

    const epochs   = opts.epochs || 100;
    const baseLR   = opts.learningRate || 0.01;
    const onProgress = opts.onProgress || (() => {});

    // Build domain vocabulary FIRST
    buildDomainVocabulary(pairs);
    updateVocabStats();

    // Create labels (unique answers)
    labels = [...new Set(pairs.map(qa => qa.a))];
    initOutputWeights(labels.length);

    // Training data
    const trainData = pairs.map(qa => ({
      input: encode(qa.q),
      target: labels.indexOf(qa.a),
      q: qa.q,
      a: qa.a
    }));

    let epoch      = 0;
    let bestLoss   = Infinity;
    let patience   = 0;
    const maxPatience = 20;

    const trainStep = () => {
      if (!isTraining) return resolve({ accuracy: 0, loss: Infinity, epochs: epoch, labels });

      if (epoch >= epochs) {
        return finish();
      }

      // Cosine-annealing LR
      const lr = baseLR * 0.5 * (1 + Math.cos(Math.PI * epoch / epochs));

      let totalLoss = 0;
      let correct   = 0;
      const shuffled = [...trainData].sort(() => Math.random() - 0.5);

      for (const sample of shuffled) {
        const { h1, h2, logits } = forward(sample.input);
        const probs = softmax(logits);

        totalLoss += crossEntropy(probs, sample.target);
        const pred = probs.indexOf(Math.max(...probs));
        if (pred === sample.target) correct++;

        // ── backprop ───
        const dLogits = probs.map((p, i) => p - (i === sample.target ? 1 : 0));
        const clipThresh = 5.0;

        // W_ho
        for (let i = 0; i < W_ho.length; i++) {
          for (let j = 0; j < C.H; j++) {
            let grad = dLogits[i] * h2[j];
            grad = Math.max(-clipThresh, Math.min(clipThresh, grad));
            V_ho[i][j] = MOMENTUM * V_ho[i][j] + lr * grad;
            W_ho[i][j] -= V_ho[i][j];
          }
        }

        // backprop → h2
        const dH2 = new Float64Array(C.H);
        for (let j = 0; j < C.H; j++) {
          let sum = 0;
          for (let i = 0; i < W_ho.length; i++) sum += W_ho[i][j] * dLogits[i];
          dH2[j] = sum * (1 - h2[j] * h2[j]);
        }

        // W_ih
        for (let i = 0; i < C.H; i++) {
          for (let j = 0; j < C.I; j++) {
            let grad = 0.5 * dH2[i] * h1[j];
            grad = Math.max(-clipThresh, Math.min(clipThresh, grad));
            V_ih[i][j] = MOMENTUM * V_ih[i][j] + lr * grad;
            W_ih[i][j] -= V_ih[i][j];
          }
        }

        // backprop → h1
        const dH1 = new Float64Array(C.I);
        for (let j = 0; j < C.I; j++) {
          let sum = 0;
          for (let i = 0; i < C.H; i++) sum += W_ih[i][j] * dH2[i];
          dH1[j] = sum * (1 - h1[j] * h1[j]);
        }

        // W_vi
        for (let i = 0; i < C.I; i++) {
          for (let j = 0; j < C.V; j++) {
            let grad = 0.3 * dH1[i] * sample.input[j];
            grad = Math.max(-clipThresh, Math.min(clipThresh, grad));
            V_vi[i][j] = MOMENTUM * V_vi[i][j] + lr * grad;
            W_vi[i][j] -= V_vi[i][j];
          }
        }
      }

      const acc     = correct / trainData.length;
      const avgLoss = totalLoss / trainData.length;

      // Early stopping
      if (avgLoss < bestLoss - 0.001) { bestLoss = avgLoss; patience = 0; }
      else patience++;

      onProgress(epoch + 1, epochs, avgLoss, acc);

      epoch++;

      if (patience >= maxPatience && acc > 0.95) {
        return finish();
      }

      if (isTraining) setTimeout(trainStep, 0);
    };

    function finish() {
      isTraining = false;
      trained    = true;
      const acc  = trainData.reduce((c, s) => {
        const { logits } = forward(s.input);
        const probs = softmax(logits);
        return c + (probs.indexOf(Math.max(...probs)) === s.target ? 1 : 0);
      }, 0) / trainData.length;
      const result = { accuracy: acc, loss: bestLoss, epochs: epoch, labels };
      if (opts.onFinish) opts.onFinish(result);
      resolve(result);
    }

    trainStep();
  });
}

// ── accessors ──────────────────────────────────────────────────────────────

export function isModelTrained()  { return trained; }
export function getLabels()       { return labels; }
export function getQAPairs()      { return qaPairs; }
export function setQAPairs(pairs) { qaPairs = pairs; }

export function getWeights() {
  return { W_vi, W_ih, W_ho };
}

export function setWeights(w) {
  W_vi = w.W_vi; W_ih = w.W_ih; W_ho = w.W_ho;
  // Re-init momentum buffers
  V_vi = W_vi.map(r => new Float64Array(r.length));
  V_ih = W_ih.map(r => new Float64Array(r.length));
  V_ho = W_ho.map(r => new Float64Array(r.length));
  trained = true;
}

export function getDomainVocabulary() {
  return { vocabulary: domainVocabulary, topics: domainTopics };
}

export function reset() {
  qaPairs = []; trained = false; labels = [];
  domainVocabulary = []; domainTopics = [];
  initWeights();
}

export function stopTraining() {
  isTraining = false;
}
